import { AdminPageHeader } from '@/components/patterns/admin-page'
import { EmptyState } from '@/components/patterns/empty-state'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import SaveBar from '@/components/forms/SaveBar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DetailCard, DetailIconBadge, DetailWell } from '@/components/patterns/detail-page'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import { Switch } from '@/components/ui/switch'
import { Typography } from '@/components/ui/typography'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import { useIsReadOnly } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { useAppForm } from '@/lib/form'
import type { AdminRouterContext } from '@/lib/router-context'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import type {
  EmailCampaign,
  EmailCampaignId,
  UpdateEmailCampaignInput,
} from '@/lib/services/email-campaigns/emailCampaigns.types'
import type { ConfigType } from 'dayjs'
import {
  emailCampaignQueries,
  useEmailCampaignPreviewMutation,
  useEmailCampaigns,
  useUpdateEmailCampaignMutation,
} from '@/lib/services/email-campaigns/emailCampaigns.query'
import { cn, getErrorMessage, toCalendarDate } from '@/lib/utils'
import { createFileRoute, notFound } from '@tanstack/react-router'
import type { DeepValue } from '@tanstack/react-form'
import { useMediaQuery } from '@uidotdev/usehooks'
import { CalendarDays, Eye, Mail, Send } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { toast } from 'sonner'

type EmailCampaignDraft = {
  id: EmailCampaignId
  enabled: boolean
  send_date: string
}

type EmailCampaignFormValues = { campaigns: EmailCampaignDraft[] }
type CampaignUpdatePayload = UpdateEmailCampaignInput & { campaignId: EmailCampaignId }
type CampaignPreview = { campaign: EmailCampaign; html: string; loading: boolean }
type EmailCampaignRowProps = {
  form: ReturnType<typeof useEmailCampaignForm>
  campaign: EmailCampaign
  index: number
  isReadOnly: boolean
  onPreview: (campaign: EmailCampaign) => void
  isSaving: boolean
  isPreviewing: boolean
  isSelected: boolean
}

export function supportsEmailMarketingForOrganizationId(
  organizationId: OrganizationIdInput
): boolean {
  return Number(organizationId) !== Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
}

export async function loadEmailMarketingRoute({
  context,
  params,
}: {
  context: Pick<AdminRouterContext, 'ability' | 'queryClient'>
  params: { orgId: number }
}): Promise<{ isReadOnly: boolean }> {
  if (!context.ability.can('list', 'email-marketing')) throw notFound()

  if (supportsEmailMarketingForOrganizationId(params.orgId)) {
    await context.queryClient.ensureQueryData(
      emailCampaignQueries.index({ organizationId: params.orgId })
    )
  }

  return { isReadOnly: !context.ability.can('edit', 'email-marketing') }
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/email-marketing/')({
  component: EmailMarketingRouteComponent,
  loader: loadEmailMarketingRoute,
})

function EmailMarketingPageHeader() {
  return (
    <AdminPageHeader
      icon={Mail}
      title="Email Marketing"
      description="Choose which holiday emails your shop sends and review each message before it goes out."
    />
  )
}

export function EmailMarketingRouteComponent() {
  const isGlobalOrg = useIsGlobalOrg()

  if (isGlobalOrg) {
    return (
      <div className="w-full space-y-5">
        <EmailMarketingPageHeader />
        <OrganizationContextNotice>
          Email campaigns are organization-specific. Manage them inside the right florist account.
        </OrganizationContextNotice>
      </div>
    )
  }

  return <EmailMarketingSettingsPage />
}

export function formatEmailCampaignDate(value: ConfigType): string {
  if (!value) return 'Date not set'

  const date = dayjs(value)

  return date.isValid() ? date.format('MMM D') : 'Date not set'
}

export function formatEmailCampaignHolidayDate(
  value: ConfigType,
  fallback = 'Date not set'
): string {
  if (!value) return fallback

  const date = dayjs(value)

  if (!date.isValid()) {
    return fallback
  }

  const day = date.date()
  const suffix =
    day % 10 === 1 && day % 100 !== 11
      ? 'st'
      : day % 10 === 2 && day % 100 !== 12
        ? 'nd'
        : day % 10 === 3 && day % 100 !== 13
          ? 'rd'
          : 'th'

  return `${date.format('MMMM')} ${day}${suffix}, ${date.format('YYYY')}`
}

export function buildEmailCampaignDatePickerBounds(value: ConfigType): {
  startMonth?: Date
  endMonth?: Date
} {
  const date = dayjs(value)

  if (!date.isValid()) {
    return {}
  }

  return {
    startMonth: toCalendarDate(date.startOf('year')),
    endMonth: toCalendarDate(date.endOf('year')),
  }
}

export function sortedEmailCampaigns<TCampaign extends Pick<EmailCampaign, 'name' | 'send_date'>>(
  campaigns: readonly TCampaign[] = []
): TCampaign[] {
  return [...campaigns].sort((a, b) => {
    const dateCompare = String(a.send_date ?? '').localeCompare(String(b.send_date ?? ''))

    if (dateCompare !== 0) return dateCompare

    return String(a.name ?? '').localeCompare(String(b.name ?? ''))
  })
}

export function buildEmailCampaignUpdatePayload(
  campaign: EmailCampaignDraft,
  patch: Partial<UpdateEmailCampaignInput> = {}
): CampaignUpdatePayload {
  return {
    campaignId: campaign.id,
    enabled: patch.enabled ?? campaign.enabled,
    send_date: patch.send_date ?? campaign.send_date,
  }
}

export function buildEmailCampaignFormValues(
  campaigns: readonly Pick<EmailCampaign, 'id' | 'enabled' | 'send_date'>[] = []
): EmailCampaignFormValues {
  return {
    campaigns: campaigns.map((campaign) => ({
      id: campaign.id,
      enabled: Boolean(campaign.enabled),
      send_date: campaign.send_date ?? '',
    })),
  }
}

export function buildEmailPreviewSrcDoc(html = ''): string {
  if (!html) return ''

  const previewPaddingStyle =
    '<style id="email-preview-padding">body{box-sizing:border-box!important;padding-top:2rem!important;}</style>'

  return html.includes('</head>')
    ? html.replace('</head>', `${previewPaddingStyle}</head>`)
    : `${previewPaddingStyle}${html}`
}

export function buildChangedEmailCampaignUpdatePayloads(
  valueCampaigns: readonly EmailCampaignDraft[] = [],
  baselineCampaigns: readonly EmailCampaignDraft[] = []
): CampaignUpdatePayload[] {
  const baselineById = new Map(baselineCampaigns.map((campaign) => [String(campaign.id), campaign]))

  return valueCampaigns
    .map((campaign) => {
      const baseline = baselineById.get(String(campaign.id))

      if (!baseline) return null

      const enabled = Boolean(campaign.enabled)
      const sendDate = campaign.send_date ?? ''

      if (enabled === Boolean(baseline.enabled) && sendDate === (baseline.send_date ?? '')) {
        return null
      }

      return buildEmailCampaignUpdatePayload(baseline, {
        enabled,
        send_date: sendDate,
      })
    })
    .filter((payload): payload is CampaignUpdatePayload => Boolean(payload))
}

function EmailCampaignRow({
  form,
  campaign,
  index,
  isReadOnly,
  onPreview,
  isSaving,
  isPreviewing,
  isSelected,
}: EmailCampaignRowProps) {
  const fieldId = `email-campaign-send-date-${campaign.id}`
  const datePickerBounds = buildEmailCampaignDatePickerBounds(campaign.send_date)

  return (
    <DetailWell
      compact
      className={cn(
        'grid gap-4 transition-colors md:grid-cols-[minmax(0,1.4fr)_minmax(12rem,0.7fr)_auto] md:items-center',
        isSelected && 'bg-primary/5 ring-primary/25'
      )}
    >
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <Typography variant="cardTitle" className="pb-0">
            {campaign.name}
          </Typography>
          <Badge variant="mutedSoft" size="sm">
            {formatEmailCampaignHolidayDate(campaign.holiday_date, campaign.month)}
          </Badge>
        </div>
        <form.Subscribe selector={(state) => state.values.campaigns?.[index]?.send_date}>
          {(sendDate) => (
            <Typography variant="supporting" className="block">
              Sends {formatEmailCampaignDate(sendDate)} at {campaign.send_time_label ?? '11:00 AM'}{' '}
              local time.
            </Typography>
          )}
        </form.Subscribe>
      </div>

      <div className="flex w-full items-center gap-2">
        <Label htmlFor={fieldId} className="sr-only">
          Send date for {campaign.name}
        </Label>
        <form.AppField<
          `campaigns[${number}].send_date`,
          DeepValue<EmailCampaignFormValues, `campaigns[${number}].send_date`>,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined
        >
          // @ts-expect-error FormCore parses dotted numeric array indices; retain the existing registered field name despite bracket-only DeepKeys.
          name={`campaigns.${index}.send_date`}
        >
          {(dateField) => (
            <div className="w-full min-w-0">
              <dateField.DatePicker
                format="MMM D"
                captionLayout="dropdown-months"
                formatters={{
                  formatYearDropdown: () => '',
                }}
                {...datePickerBounds}
                className="w-full min-w-0"
                buttonProps={{
                  id: fieldId,
                  disabled: isReadOnly || isSaving,
                }}
              />
            </div>
          )}
        </form.AppField>
      </div>

      <div className="flex items-center justify-between gap-3 md:justify-end">
        <form.AppField<
          `campaigns[${number}].enabled`,
          DeepValue<EmailCampaignFormValues, `campaigns[${number}].enabled`>,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined
        >
          // @ts-expect-error FormCore parses dotted numeric array indices; retain the existing registered field name despite bracket-only DeepKeys.
          name={`campaigns.${index}.enabled`}
        >
          {(enabledField) => (
            <div className="flex items-center gap-2">
              <Switch
                aria-label={`${enabledField.state.value ? 'Disable' : 'Enable'} ${campaign.name}`}
                checked={enabledField.state.value}
                disabled={isReadOnly || isSaving}
                onCheckedChange={enabledField.handleChange}
              />
              <Typography variant="label" tone="muted" className="w-16">
                {enabledField.state.value ? 'Enabled' : 'Off'}
              </Typography>
            </div>
          )}
        </form.AppField>

        <Button
          type="button"
          variant={isSelected ? 'selection' : 'outline'}
          size="sm"
          disabled={isPreviewing}
          onClick={() => onPreview(campaign)}
        >
          <Eye className="size-4" />
          Preview
        </Button>
      </div>
    </DetailWell>
  )
}

function EmailPreviewFrame({
  preview,
  frameClassName = '',
}: {
  preview: CampaignPreview | null
  frameClassName?: string
}) {
  if (!preview) {
    return (
      <EmptyState
        className="h-full min-h-[28rem]"
        icon={Eye}
        surface="transparent"
        title="Preview an email"
        description="Choose a holiday email to preview it before it goes out."
      />
    )
  }

  if (preview.loading) {
    return (
      <div className="flex h-full min-h-[28rem] items-center justify-center rounded-xl border border-border/70 bg-muted/10 text-sm text-muted-foreground">
        Loading preview...
      </div>
    )
  }

  return (
    <div className="h-full min-h-[40rem] overflow-hidden rounded-xl border bg-white">
      <iframe
        title={`${preview.campaign?.name ?? 'Holiday'} email preview`}
        srcDoc={buildEmailPreviewSrcDoc(preview.html ?? '')}
        className={cn('h-full min-h-[40rem] w-full bg-white', frameClassName)}
      />
    </div>
  )
}

function EmailMarketingSplitLayout({
  schedulePanel,
  previewPanel,
  showInlinePreview,
}: {
  schedulePanel: ReactNode
  previewPanel: ReactNode
  showInlinePreview: boolean
}) {
  if (!showInlinePreview) {
    return <div>{schedulePanel}</div>
  }

  return (
    <ResizablePanelGroup
      direction="horizontal"
      autoSaveId="email-marketing-layout-v2"
      className="min-h-[calc(100dvh-12rem)] w-full items-stretch"
    >
      <ResizablePanel defaultSize={50} minSize={40} className="min-w-[34rem]">
        <div className="h-full min-h-0 pr-2">{schedulePanel}</div>
      </ResizablePanel>

      <ResizableHandle
        withHandle
        className="mx-1 w-2 rounded-full bg-transparent transition-colors hover:bg-primary/15"
      />

      <ResizablePanel defaultSize={50} minSize={24} className="min-w-[22rem]">
        <div className="h-full min-h-0 pl-2">{previewPanel}</div>
      </ResizablePanel>
    </ResizablePanelGroup>
  )
}

function useEmailCampaignForm(
  defaultValues: EmailCampaignFormValues,
  updateMutation: Pick<ReturnType<typeof useUpdateEmailCampaignMutation>, 'mutateAsync'>
) {
  return useAppForm({
    defaultValues,
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        const changedCampaigns = buildChangedEmailCampaignUpdatePayloads(
          value.campaigns,
          defaultValues.campaigns
        )

        if (!changedCampaigns.length) {
          formApi.reset(value)
          return
        }

        const updateResults = await Promise.allSettled(
          changedCampaigns.map((payload) => updateMutation.mutateAsync(payload))
        )
        const firstError: unknown = updateResults.find(
          (result) => result.status === 'rejected'
        )?.reason

        if (firstError) {
          toast.error('Email campaigns were not saved', {
            description: getErrorMessage(firstError),
          })
          return
        }

        formApi.reset(value)
        toast.success(
          changedCampaigns.length === 1 ? 'Email campaign saved' : 'Email campaigns saved'
        )
      },
    },
  })
}

export function EmailMarketingSettingsPage() {
  const orgId = useRouteOrganizationIdParam()
  const { isReadOnly } = useIsReadOnly()
  const { data } = useEmailCampaigns({ organizationId: orgId })
  const updateMutation = useUpdateEmailCampaignMutation()
  const {
    mutateAsync: loadEmailCampaignPreview,
    isPending: isPreviewPending,
    variables: previewVariables,
  } = useEmailCampaignPreviewMutation()
  const showInlinePreview = useMediaQuery('(min-width: 1280px)')
  const campaigns = useMemo(() => sortedEmailCampaigns(data?.campaigns ?? []), [data?.campaigns])
  const firstCampaign = campaigns[0] ?? null
  const [preview, setPreview] = useState<CampaignPreview | null>(() =>
    firstCampaign
      ? {
          campaign: firstCampaign,
          html: '',
          loading: true,
        }
      : null
  )
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false)
  const autoPreviewCampaignIdRef = useRef<EmailCampaignId | null>(null)
  const previewRequestIdRef = useRef(0)
  const defaultValues = useMemo(() => buildEmailCampaignFormValues(campaigns), [campaigns])
  const form = useEmailCampaignForm(defaultValues, updateMutation)

  const handlePreview = useCallback(
    async (campaign: EmailCampaign, { openDialog = true } = {}) => {
      if (openDialog) {
        setIsPreviewDialogOpen(true)
      }

      if (preview?.campaign?.id === campaign.id && preview.loading) {
        return
      }

      const requestId = previewRequestIdRef.current + 1
      previewRequestIdRef.current = requestId

      setPreview({
        campaign,
        html: '',
        loading: true,
      })

      try {
        const response = await loadEmailCampaignPreview({ campaignId: campaign.id })

        if (previewRequestIdRef.current !== requestId) {
          return
        }

        setPreview({
          campaign,
          html: response?.html ?? '',
          loading: false,
        })
      } catch (error) {
        if (previewRequestIdRef.current !== requestId) {
          return
        }

        setPreview(null)
        toast.error('Email preview was not loaded', {
          description: getErrorMessage(error),
        })
      }
    },
    [loadEmailCampaignPreview, preview]
  )

  useEffect(() => {
    if (!firstCampaign) {
      return
    }

    if (autoPreviewCampaignIdRef.current === firstCampaign.id) {
      return
    }

    autoPreviewCampaignIdRef.current = firstCampaign.id
    const requestId = previewRequestIdRef.current + 1
    previewRequestIdRef.current = requestId
    let isCurrent = true

    loadEmailCampaignPreview({ campaignId: firstCampaign.id })
      .then((response) => {
        if (!isCurrent || previewRequestIdRef.current !== requestId) {
          return
        }

        setPreview({
          campaign: firstCampaign,
          html: response?.html ?? '',
          loading: false,
        })
      })
      .catch((error: unknown) => {
        if (!isCurrent || previewRequestIdRef.current !== requestId) {
          return
        }

        setPreview(null)
        toast.error('Email preview was not loaded', {
          description: getErrorMessage(error),
        })
      })

    return () => {
      isCurrent = false

      if (
        autoPreviewCampaignIdRef.current === firstCampaign.id &&
        previewRequestIdRef.current === requestId
      ) {
        autoPreviewCampaignIdRef.current = null
      }
    }
  }, [firstCampaign, loadEmailCampaignPreview])

  const handlePreviewDialogOpenChange = (open: boolean) => {
    setIsPreviewDialogOpen(open)
  }

  const handlePreviewClick = (campaign: EmailCampaign) => {
    void handlePreview(campaign)
  }

  const schedulePanel = (
    <DetailCard accent="mint" className="h-full" contentClassName="space-y-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <DetailIconBadge icon={Send} tone="mint" />
          <div className="min-w-0 space-y-1">
            <Typography variant="eyebrow">Holiday Emails</Typography>
            <Typography variant="h4" className="pb-0">
              Campaign schedule
            </Typography>
            <Typography variant="supporting" className="block">
              Enabled holidays send at {data?.send_time_label ?? '11:00 AM'} in your primary
              location timezone.
            </Typography>
          </div>
        </div>
        <Badge variant="mutedSoft" size="sm">
          {campaigns.length} emails
        </Badge>
      </div>

      <DetailWell compact className="flex items-center gap-2 text-sm text-muted-foreground">
        <CalendarDays className="size-4" />
        <span>{campaigns.length} holiday emails are available for the next send cycle.</span>
      </DetailWell>

      <div className="space-y-3">
        {campaigns.map((campaign, index) => (
          <EmailCampaignRow
            key={campaign.id}
            form={form}
            campaign={campaign}
            index={index}
            isReadOnly={isReadOnly}
            onPreview={handlePreviewClick}
            isSaving={updateMutation.isPending}
            isPreviewing={isPreviewPending && previewVariables?.campaignId === campaign.id}
            isSelected={preview?.campaign?.id === campaign.id}
          />
        ))}
      </div>
    </DetailCard>
  )

  const previewPanel = (
    <aside className="hidden h-full min-h-0 xl:block">
      <DetailCard
        accent="periwinkle"
        className="h-full"
        contentClassName="flex h-full min-h-0 flex-col space-y-4 p-5"
      >
        <div className="flex items-start gap-3">
          <DetailIconBadge icon={Eye} tone="periwinkle" />
          <div className="min-w-0 space-y-1">
            <Typography variant="eyebrow">Preview</Typography>
            <Typography variant="h5" className="pb-0">
              {preview?.campaign?.name ?? 'Email preview'}
            </Typography>
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <EmailPreviewFrame preview={preview} />
        </div>
      </DetailCard>
    </aside>
  )

  return (
    <div className="w-full space-y-5">
      <EmailMarketingPageHeader />

      <form.AppForm>
        <form.Form>
          <EmailMarketingSplitLayout
            schedulePanel={schedulePanel}
            previewPanel={previewPanel}
            showInlinePreview={showInlinePreview}
          />

          {!isReadOnly ? <SaveBar mutation={updateMutation} /> : null}
        </form.Form>
      </form.AppForm>

      <Dialog
        open={Boolean(preview) && !showInlinePreview && isPreviewDialogOpen}
        onOpenChange={handlePreviewDialogOpenChange}
      >
        <DialogContent size="canvas" className="h-[88vh] grid-rows-[auto_minmax(0,1fr)]">
          <DialogHeader>
            <DialogTitle>{preview?.campaign?.name ?? 'Email'} Preview</DialogTitle>
          </DialogHeader>

          <EmailPreviewFrame preview={preview} frameClassName="h-full min-h-0 xl:h-full" />
        </DialogContent>
      </Dialog>
    </div>
  )
}
